/**
 * #1893 step 1 — the SA phone sheet: who, across every local, has consented to
 * having calls forwarded to them.
 *
 * ⚠ THE ROW IS A POSITION, NOT A PERSON, and that is the whole correctness
 * question here. On iatse24 one person holds Admin Contact, Business Agent and
 * Dispatch with the same number on all three, and only the Business Agent row
 * has `forward_calls`. A sheet keyed by person — or deduped by number, which
 * looks like tidying — turns one consent into three and starts forwarding calls
 * somebody never agreed to take.
 *
 * Consent is read at request time and never cached: a local clearing the box
 * must change on the next load, not on the next deploy.
 *
 * ══ EVERY CONTACT IS SHOWN; CONSENT IS A TAG, NOT A FILTER (ed, on the screen) ══
 *
 * The first cut listed only consented contacts, so the sheet read "2 rows" when
 * the locals had entered 16. That looks like a broken page rather than like
 * fourteen people who have not opted in — and the rows a local most needs to see
 * are exactly the ones that were hidden: the consent with no number, and the
 * position nobody has opted in yet.
 */

/** Digits only, so formatting in the stored value cannot change the answer. */
const digits = (v) => String(v === null || v === undefined ? '' : v).replace(/\D+/g, '');

/**
 * E.164 for display.
 *
 * ⚠ NEVER INVENT A COUNTRY CODE. A 10-digit NANP number takes +1 and a
 * `.ca` local is +1 as well, so that default is safe — but a value that is not
 * a NANP number is returned untouched. Prefixing +1 onto nine digits produces a
 * plausible, dialable, WRONG number, and somebody rings a stranger.
 */
export function toE164(value) {
    const raw = String(value === null || value === undefined ? '' : value).trim();
    if (raw === '') {
        return '';
    }
    if (raw.charAt(0) === '+') {
        return raw.replace(/[^\d+]/g, '');
    }

    const d = digits(raw);
    if (d.length === 10) {
        return '+1' + d;
    }
    if (d.length === 11 && d.charAt(0) === '1') {
        return '+' + d;
    }

    return raw;   // unreadable — show what is stored, do not guess
}

/** Can this be dialled as written, or does a human need to look at it? */
export function isDialable(value) {
    const d = digits(value);

    return d.length === 10 || (d.length === 11 && d.charAt(0) === '1');
}

/** The three states a contact can be in, and there are only three. */
export const FORWARDING_TAGS = ['allowed', 'not allowed', 'no number'];

/**
 * Why a contact can or cannot be called.
 *
 * ⚠ CONSENT IS THE GOVERNING FACT. Someone with neither consent nor a number
 * reads "not allowed", not "no number": the missing number is not what is
 * stopping the call, and reporting it as the blocker sends somebody to fix the
 * wrong field.
 */
export function tagFor(row) {
    if (!row || row.forward_calls !== true) {
        return 'not allowed';
    }

    return String(row.phone || '').trim() === '' ? 'no number' : 'allowed';
}

/**
 * Every contact, tagged.
 *
 * Nothing is filtered out here any more. `callable` is the one flag that still
 * means "this call can actually be placed", and only `allowed` earns it.
 */
export function sheetRows(rows) {
    return (rows || [])
        .filter((r) => !!r)
        .map((r) => {
            const forwarding = tagFor(r);

            return {
                id: r.id,
                local: r.local,
                position: r.position,
                contact: r.contact || '',
                phone: r.phone,
                e164: toE164(r.phone),
                dialable: isDialable(r.phone),
                forwarding,
                callable: forwarding === 'allowed',
            };
        });
}

/**
 * ⚠ What a row IS. The person is a detail on the row, never its identity — see
 * the note at the top of this file.
 */
export function rowLabel(row) {
    return `${row.local} · ${row.position}`;
}

/**
 * #1917 — the class for the Forwarding CELL.
 *
 * ed: highlight it green when forwarding is allowed. The pill was already green
 * but it is an 11px chip, and only 2 of 16 rows qualify — so the rows that
 * matter were the hardest to find on the page.
 *
 * ⚠ AN ADDITION, NEVER THE CARRIER. The tag text stays and stays correct, so a
 * reader who cannot distinguish the green — colour blindness, a projector, a
 * printout, a screenshot pasted into a ticket — loses nothing. A sheet that says
 * who may be called must not encode that in hue alone.
 *
 * ⚠ Follows `callable`, not consent alone: a consented contact with no number is
 * not callable and must not be lit up as though somebody could ring them.
 */
export function cellClassFor(row) {
    return row && row.callable ? 'ps-cell--allowed' : '';
}

/** The locals present, for the filter. */
export function localsIn(rows) {
    const seen = {};
    (rows || []).forEach((r) => { if (r && r.local) { seen[r.local] = true; } });

    return Object.keys(seen).sort();
}

export function filterByLocal(rows, local) {
    if (!local) {
        return rows || [];
    }

    return (rows || []).filter((r) => r.local === local);
}

/**
 * ⚠ NOW THIS MEANS NO CONTACTS AT ALL, not "none opted in" — the sheet lists
 * every contact, so an empty one means no local has entered any. "No data"
 * reads as a broken page and sends someone looking for a bug.
 */
export function emptyMessage() {
    return 'No local has entered a contact yet. '
        + 'Contacts are added per position under Config → Contact info, and forwarding '
        + 'is opted into there by ticking "forward calls" on a contact that has a number.';
}
